import { useEffect, useState, useCallback, useRef } from 'react';

const DEFAULT_GLYPHS = '!@#$%^&*()_+-=[]{}|;:,.<>?/~0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function TextDecode({
  as: Element = 'div',
  text = '',
  intervalMs = 40,
  lockDelayFrames = 13,
  glyphs = DEFAULT_GLYPHS,
  triggerOnHover = true,
  initDelay = 300,
  children,
  ...props
}) {
  const [displayedText, setDisplayedText] = useState(text);
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);
  const initTimerRef = useRef(null);

  const startDecoding = useCallback(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setDisplayedText(text);
      return;
    }

    if (timerRef.current) clearInterval(timerRef.current);
    if (initTimerRef.current) clearTimeout(initTimerRef.current);

    let frameCount = 0;
    setIsAnimating(true);

    const tick = () => {
      frameCount += 1;
      let allSettled = true;

      const nextText = text
        .split('')
        .map((char, i) => {
          const charFrameDiff = frameCount - i;
          if (charFrameDiff >= lockDelayFrames) {
            return char;
          }
          allSettled = false;
          if (char === ' ') return ' ';
          const randomGlyph = glyphs[Math.floor(Math.random() * glyphs.length)];
          return randomGlyph;
        })
        .join('');

      setDisplayedText(nextText);

      if (allSettled) {
        if (timerRef.current) clearInterval(timerRef.current);
        setDisplayedText(text);
        setIsAnimating(false);
      }
    };

    if (initDelay > 0) {
      initTimerRef.current = setTimeout(() => {
        timerRef.current = setInterval(tick, intervalMs);
      }, initDelay);
    } else {
      timerRef.current = setInterval(tick, intervalMs);
    }
  }, [text, intervalMs, lockDelayFrames, glyphs, initDelay]);

  useEffect(() => {
    startDecoding();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (initTimerRef.current) clearTimeout(initTimerRef.current);
    };
  }, [startDecoding]);

  const handleMouseEnter = (e) => {
    if (props.onMouseEnter) props.onMouseEnter(e);
    if (triggerOnHover && !isAnimating) {
      startDecoding();
    }
  };

  return (
    <Element {...props} onMouseEnter={handleMouseEnter}>
      {displayedText}
      <span aria-hidden="true">&nbsp;</span>
    </Element>
  );
}

export default TextDecode;
